// What the import wizard's steps and its shell share: the answers' types,
// the plain functions that decide what a step starts with, and the draft the
// tab keeps. Kept out of the step files so no step imports another step, and
// so a node test can call these without drawing anything.

import type {
  CommitResult,
  ImportAnalysis,
  ImportColumnChoices,
  ImportCounts,
  ImportDuplicates,
  ImportPreviewFilter,
  ImportQuestion,
  ImportValueSet,
} from '../../lib/api'
import { KEEP_EXTRA, LEAVE_OUT, plural } from '../../lib/importWords'
import type { StatusWord } from '../../lib/status'

// Per field key: the file's value to the option code chosen for it. A value
// with no entry has no answer yet. An option whose code is "" is an answer.
export type ValueAnswers = Record<string, Record<string, string>>

export const NEEDS_ANSWER: StatusWord = { word: 'Needs an answer', tone: 'warn', icon: 'alert' }

// ---------------------------------------------------------------- helpers

export function initialChoices(a: ImportAnalysis, before: ImportColumnChoices = {}): ImportColumnChoices {
  const known = new Set(a.fields.map((f) => f.key))
  const out: ImportColumnChoices = {}
  for (const c of a.columns) {
    const kept = before[c.column]
    if (kept !== undefined && (kept === KEEP_EXTRA || kept === LEAVE_OUT || known.has(kept))) {
      out[c.column] = kept
    } else if (c.field && known.has(c.field)) {
      out[c.column] = c.field
    } else {
      out[c.column] = c.keep === 'LEAVE' ? LEAVE_OUT : KEEP_EXTRA
    }
  }
  return out
}

// Every question the API sent, by key, whatever the key is: the ones it asks
// and the ones the file already settled (asked: false), which are not shown
// but go back with their answer. An answer the person gave before is kept only
// for a question still asked and only when it is still one of its options; a
// question the new reading settles takes the file's answer. An option whose
// code is "" is a real answer, so "" is never read as "not answered" here.
export function initialAnswers(a: ImportAnalysis, before: Record<string, string> = {}): Record<string, string> {
  const out: Record<string, string> = {}
  for (const q of a.questions ?? []) {
    const had = before[q.key]
    const keep = q.asked && had !== undefined && (q.options ?? []).some((o) => o.code === had)
    out[q.key] = keep ? had : (q.answer ?? '')
  }
  return out
}

// The asked questions with no answer among their options yet.
export function openQuestions(a: ImportAnalysis, answers: Record<string, string>): ImportQuestion[] {
  return (a.questions ?? []).filter(
    (q) => q.asked && !(q.options ?? []).some((o) => o.code === (answers[q.key] ?? q.answer)),
  )
}

// The suggestions, with any answer already given for the same word kept.
export function prefillValues(sets: ImportValueSet[], before: ValueAnswers): ValueAnswers {
  const out: ValueAnswers = {}
  for (const s of sets) {
    const mine = (out[s.field] ??= {})
    for (const v of s.values) {
      const had = before[s.field]?.[v.value]
      if (had !== undefined && s.options.some((o) => o.code === had)) mine[v.value] = had
      else if (v.suggested) mine[v.value] = v.code
    }
  }
  return out
}

export function valuesBody(sets: ImportValueSet[], answers: ValueAnswers): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {}
  for (const s of sets) {
    for (const v of s.values) {
      const a = answers[s.field]?.[v.value]
      if (a !== undefined) (out[s.field] ??= {})[v.value] = a
    }
  }
  return out
}

export function addLabel(c: ImportCounts): string {
  return 'Add ' + plural(c.add, 'vendor') + (c.update > 0 ? ' and update ' + c.update.toLocaleString('en-IN') : '')
}

// What the tab keeps of an import between steps and across a refresh
// (lib/importDraft.ts). The file itself cannot be kept; what the server read
// from it can, and that is all the later steps use.
export type ImportDraft = {
  analysis: ImportAnalysis
  choices: ImportColumnChoices
  confirmed: Record<string, boolean>
  answers: Record<string, string>
  valueSets: ImportValueSet[] | null
  valuesFor: string
  valueAnswers: ValueAnswers
  skippedValues: boolean
  duplicates: ImportDuplicates
  mappedFor: string
  counts: ImportCounts | null
  mapNote: string
  filter: ImportPreviewFilter
  committed: CommitResult | null
  // Release 3.3 (I2). Optional: a tab may hold a draft kept before them.
  // The columns a person chose by hand, "Remember this mapping as", the
  // values to fill in, the server draft's version and what was last written
  // to it, and the matching last saved as a mapping.
  byPerson?: string[]
  remember?: { on: boolean; name: string }
  fill?: Record<string, string>
  draftVersion?: number
  sentSig?: string
  savedFor?: string
  // Release 3.3 (I4): the page of Review and fix (from 0) and the file
  // columns that carry a fix.
  page?: number
  fixedColumns?: string[]
}

// The furthest step the kept answers reach: an address can ask for a step the
// tab never got to, and is then shown the last one it did.
export function reachableStep(
  want: number,
  d: { analysis: ImportAnalysis | null; valueSets: ImportValueSet[] | null; skippedValues: boolean; counts: ImportCounts | null } | null,
): number {
  if (!d?.analysis) return 1
  let top = 2
  if (d.valueSets && !d.skippedValues) top = 3
  if (d.counts) top = 5
  const n = Math.min(Math.max(want, 1), top)
  return n === 3 && (!d.valueSets || d.skippedValues) ? 2 : n
}
